import {
  Badge,
  Button,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import {
  BudgetItemTypeEnum,
  ExpenseBudgetItemTypeEnum,
  ExpenseItemsTimeBillingOrderByColumn,
  OiBudgetItemsTimeBillingOrderByColumn,
} from "@src/__generated__/graphql";
import {
  ACTIONS_COLUMN_ID,
  type BodyColumnDef,
  Link,
} from "@src/components/ui-kit";
import { DataTable } from "@src/components/ui-kit/DataTable";
import { LoadingOverlay } from "@src/components/ui-kit/LoadingOverlay/LoadingOverlay";
import { Pagination } from "@src/components/ui-kit/table/pagination/Pagination";
import { FiltersPanel } from "@src/utils/components/filters/components/filters-panel";
import { currency, formatDate } from "@src/utils/formatters";
import { observer } from "mobx-react-lite";
import { encodeOiBudgetItemKey } from "./oi-budget-item-key";
import type { SourcesEditModalStore } from "./store";

type Props = {
  readonly store: SourcesEditModalStore;
};

type OiBudgetItem = NonNullable<
  SourcesEditModalStore extends { displayedOiBudgetItems: infer T } ? T : never
>[number];

type ExpenseItemForModal = NonNullable<
  SourcesEditModalStore extends { displayedExpenseItems: infer T } ? T : never
>[number];

type OiBudgetItemRow = OiBudgetItem & { id: string };

const getOiBudgetItemColumns = (): BodyColumnDef<
  OiBudgetItemRow,
  OiBudgetItemsTimeBillingOrderByColumn
>[] => [
  {
    id: "invoice_no",
    meta: { label: t`Invoice No` },
    header: t`Invoice No`,
    orderBy: OiBudgetItemsTimeBillingOrderByColumn.InvoiceNo,
    cell: ({ row }) => (
      <Link
        href={{
          pathname: "/invoices/outgoing-invoices/detail",
          query: { id: row.original.outgoing_invoice_id },
        }}
        target="_blank"
      >
        {row.original.outgoingInvoice.invoice_no}
      </Link>
    ),
  },
  {
    id: "budget_item",
    meta: { label: t`Budget Item` },
    header: t`Budget Item`,
    orderBy: OiBudgetItemsTimeBillingOrderByColumn.BudgetItem,
    cell: ({ row }) => {
      const title = row.original.budgetItem?.title ?? "-";
      const isExpense = row.original.budget_item_type === "EXPENSE_BUDGET_ITEM";
      return (
        <HStack spacing="2">
          <Text>{title}</Text>
          <Badge colorScheme={isExpense ? "orange" : "blue"} size="sm">
            {isExpense ? t`Expense` : t`Work`}
          </Badge>
        </HStack>
      );
    },
  },
  {
    id: "issue_date",
    meta: { label: t`Date` },
    header: t`Date`,
    orderBy: OiBudgetItemsTimeBillingOrderByColumn.IssueDate,
    cell: ({ row }) =>
      formatDate(new Date(row.original.outgoingInvoice.issue_date)),
  },
  {
    id: "amount",
    meta: { label: t`Amount`, align: "right" },
    header: t`Amount`,
    orderBy: OiBudgetItemsTimeBillingOrderByColumn.Amount,
    cell: ({ row }) => currency.formatByWorkspace(row.original.amount),
  },
  {
    id: "billing_address",
    meta: { label: t`Billing Address` },
    header: t`Billing Address`,
    orderBy: OiBudgetItemsTimeBillingOrderByColumn.BillingAddress,
    cell: ({ row }) => {
      const be = row.original.outgoingInvoice.billingEntity;
      const parts = [be?.internal_name, be?.street, be?.city].filter(Boolean);
      return parts.join(", ") || "-";
    },
  },
  {
    id: ACTIONS_COLUMN_ID,
    meta: { label: t`Actions` },
  },
];

const getExpenseItemColumns = (): BodyColumnDef<
  ExpenseItemForModal,
  ExpenseItemsTimeBillingOrderByColumn
>[] => [
  {
    id: "expense_no",
    meta: { label: t`Expense No` },
    header: t`Expense No`,
    orderBy: ExpenseItemsTimeBillingOrderByColumn.ExpenseNo,
    cell: ({ row }) => (
      <Link
        href={{
          pathname: "/expenses/edit",
          query: { id: row.original.expense?.id },
        }}
        target="_blank"
      >
        {row.original.expense?.expense_no ??
          row.original.expense?.document_number ??
          row.original.id}
      </Link>
    ),
  },
  {
    id: "budget_item",
    meta: { label: t`Budget Item` },
    header: t`Budget Item`,
    orderBy: ExpenseItemsTimeBillingOrderByColumn.BudgetItem,
    cell: ({ row }) => {
      if (!row.original.budgetItem) return "-";
      const title = row.original.budgetItem.title;
      const isExpense =
        row.original.budget_item_type ===
        ExpenseBudgetItemTypeEnum.ExpenseBudgetItem;
      return (
        <HStack spacing="2">
          <Text>{title}</Text>
          <Badge colorScheme={isExpense ? "orange" : "blue"} size="sm">
            {isExpense ? t`Expense` : t`Work`}
          </Badge>
        </HStack>
      );
    },
  },
  {
    id: "contractor",
    meta: { label: t`Contractor` },
    header: t`Contractor`,
    orderBy: ExpenseItemsTimeBillingOrderByColumn.ContractorName,
    cell: ({ row }) => row.original.expense?.contractor?.legal_name ?? "-",
  },
  {
    id: "description",
    meta: { label: t`Description` },
    header: t`Description`,
    orderBy: ExpenseItemsTimeBillingOrderByColumn.Description,
    cell: ({ row }) => {
      const description = row.original.description?.title;

      if (!description) return "-";
      if (description.length <= 15) return description;

      return (
        <Tooltip
          hasArrow
          label={description}
          placement="top"
          shouldWrapChildren
        >
          <Text>{description.slice(0, 15)}...</Text>
        </Tooltip>
      );
    },
  },
  {
    id: "date",
    meta: { label: t`Date` },
    header: t`Date`,
    orderBy: ExpenseItemsTimeBillingOrderByColumn.DateOfSupply,
    cell: ({ row }) =>
      row.original.expense?.date_of_supply
        ? formatDate(new Date(row.original.expense.date_of_supply))
        : "-",
  },
  {
    id: "amount",
    meta: { label: t`Amount`, align: "right" },
    header: t`Amount`,
    orderBy: ExpenseItemsTimeBillingOrderByColumn.Amount,
    cell: ({ row }) => currency.formatByWorkspace(row.original.amount),
  },
  {
    id: ACTIONS_COLUMN_ID,
    meta: { label: t`Actions` },
  },
];

export const SourcesEditModal = observer(function SourcesEditModal({
  store,
}: Props) {
  const oiBudgetItemRows: OiBudgetItemRow[] = store.displayedOiBudgetItems.map(
    (item) => ({
      ...item,
      id: encodeOiBudgetItemKey(
        item.outgoing_invoice_id,
        item.budget_item_id,
        item.budget_item_type as Parameters<typeof encodeOiBudgetItemKey>[2],
      ),
    }),
  );

  return (
    <Modal
      isOpen={store.disclosureState.isOpen}
      onClose={store.disclosureState.onClose}
      size="6xl"
      trapFocus={false}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <HStack justify="space-between" pr="8">
            <Text>{store.modalTitle}</Text>
            <HStack spacing="2">
              <Button onClick={store.disclosureState.onClose} variant="ghost">
                <Trans>Cancel</Trans>
              </Button>
              <Button onClick={store.handleConfirm}>
                <Trans>Confirm</Trans>
              </Button>
            </HStack>
          </HStack>
        </ModalHeader>

        <ModalCloseButton />

        <ModalBody>
          {store.sourcesModalType === BudgetItemTypeEnum.OurWorkBudgetItem ? (
            <DataTable>
              <DataTable.Filter>
                <FiltersPanel
                  filters={store.oiBudgetItemFilters}
                  onSearch={store.setOiBudgetItemSearchTerm}
                  searchDefaultValue={store.oiBudgetItemSearchTerm}
                />
              </DataTable.Filter>

              <LoadingOverlay
                isLoading={store.isLoadingOiBudgetItems}
                position="absolute"
                size="md"
              >
                <DataTable.Body
                  tableKey="sources-edit-oi-budget-items"
                  data={oiBudgetItemRows}
                  columns={getOiBudgetItemColumns()}
                  enableRowSelection
                  orderedBy={store.oiBudgetItemOrderBy.clauses[0]}
                  onOrderByChange={store.handleOiBudgetItemOrderChange}
                  selected={store.selectedOiBudgetItemKeys}
                  onSelect={store.setSelectedOiBudgetItemKeys}
                  bulkActionsInfoBlock={
                    <Text>
                      <Trans>Total:</Trans>
                      &nbsp;
                      <strong>
                        {currency.formatByWorkspace(store.selectedTotalAmount)}
                      </strong>
                    </Text>
                  }
                />
              </LoadingOverlay>

              <Pagination store={store.pagination} />
            </DataTable>
          ) : (
            <DataTable>
              <DataTable.Filter>
                <FiltersPanel
                  filters={store.expenseItemFilters}
                  onSearch={store.setExpenseSearchTerm}
                  searchDefaultValue={store.expenseSearchTerm}
                />
              </DataTable.Filter>

              <LoadingOverlay
                isLoading={store.isLoadingExpenseItems}
                position="absolute"
                size="md"
              >
                <DataTable.Body
                  tableKey="sources-edit-expense-items"
                  data={store.displayedExpenseItems}
                  columns={getExpenseItemColumns()}
                  enableRowSelection
                  orderedBy={store.expenseOrderBy.clauses[0]}
                  onOrderByChange={store.handleExpenseOrderChange}
                  selected={store.selectedExpenseItemIds}
                  onSelect={store.setSelectedExpenseItemIds}
                  bulkActionsInfoBlock={
                    <Text fontWeight="medium">
                      <Trans>Total:</Trans>
                      &nbsp;
                      <strong>
                        {currency.formatByWorkspace(store.selectedTotalAmount)}
                      </strong>
                    </Text>
                  }
                />
              </LoadingOverlay>

              <Pagination store={store.pagination} />
            </DataTable>
          )}
        </ModalBody>

        <ModalFooter>
          <Button onClick={store.disclosureState.onClose} variant="ghost">
            <Trans>Cancel</Trans>
          </Button>
          <Button ml="2" onClick={store.handleConfirm}>
            <Trans>Confirm</Trans>
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
});
